/**
 * Labstep
 *
 * @module grid/BatchCreateResourceItems/components/ColumnHeader
 * @desc Column Header for BatchCreateResourceItems grid
 */

import { plainToInstance } from 'class-transformer';
import classnames from 'classnames';
import { MetadataActionCreateModalContainer as MetadataActionCreateModal } from 'labstep-web/components/Metadata/Action/Create/Modal';
import { useGridBatchCreateResourceItemsServiceFromContext } from 'labstep-web/contexts/grid/hook';
import { Icon } from 'labstep-web/core/Icon';
import { getMetadataColDef } from 'labstep-web/grid/BatchCreateResourceItems/coldefs/metadata';
import { useBatchCreateItemContext } from 'labstep-web/grid/BatchCreateResourceItems/context';
import { Metadata } from 'labstep-web/models/metadata';
import type { Resource } from 'labstep-web/models/resource.model';
import type { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';
import type { SemanticICONS } from 'semantic-ui-react';
import styles from './styles.module.scss';

interface GridBatchCreateResourceItemsColumnHeaderProps {
  displayName?: string;
  icon?: SemanticICONS;
  isAddColumn?: boolean;
  resource?: Resource;
  column?: any;
}

export const GridBatchCreateResourceItemsColumnHeader: React.FC<
  GridBatchCreateResourceItemsColumnHeaderProps
> = ({ displayName, icon, isAddColumn = false, resource }) => {
  const gridService =
    useGridBatchCreateResourceItemsServiceFromContext();
  const context = useBatchCreateItemContext();

  // Fetch the item template directly from Redux store

  if (!resource) {
    return null;
  }

  // Get the item template ID (either from resource or its template)
  const itemTemplate =
    resource.resource_item_template ||
    resource.template?.resource_item_template;

  if (!itemTemplate) {
    return null;
  }

  if (isAddColumn && resource) {
    if (!itemTemplate) {
      return (
        <div
          className={classnames(styles.container, styles.disabled)}
        >
          <div className={styles.actionTitle}>
            {icon && <Icon name={icon} />}{' '}
            {displayName || 'Add Column'}
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <MetadataActionCreateModal
          entity={itemTemplate as ResourceItem}
          entityName="item"
          isTemplate={true}
          actionComponentProps={{
            type: 'text',
            icon: icon || 'plus',
            elementProps: {
              placeholder: true,
            },
          }}
          options={{
            onSuccess: ({ response }) => {
              if (!response || !gridService || !context) {
                return;
              }

              // Get the newly created metadata from the response
              const metadataData =
                response.entities.metadata[response.result as number];

              if (!metadataData) {
                return;
              }

              // Transform the plain data into a Metadata model instance
              const newMetadata = plainToInstance(
                Metadata,
                metadataData,
              );

              const { data, setData, dateFormats } = context;

              // Get current column definitions
              const currentColDefs =
                gridService.agGrid.api.getColumnDefs() || [];

              // Find the "Add Column" button column (should be the last one)
              const addColumnIndex = currentColDefs.findIndex(
                (col: any) => col.colId === 'add_column',
              );

              // Create the new column definition for the metadata field
              const newColDef = getMetadataColDef(
                newMetadata,
                resource,
                data,
                setData,
                dateFormats,
              );

              // Insert the new column before the "Add Column" button
              const newColDefs = [...currentColDefs];
              if (addColumnIndex !== -1) {
                newColDefs.splice(addColumnIndex, 0, newColDef);
              } else {
                newColDefs.push(newColDef);
              }

              // Update the grid with new column definitions
              gridService.agGrid.api.setColumnDefs(newColDefs);
            },
          }}
          isHeaderPlural={false}
          disableAddAnother={true}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        {icon && <Icon name={icon} />} {displayName}
      </div>
    </div>
  );
};
